// frontend/src/utils/logger.js

/**
 * ================================================================================
 * 🔧 Advanced Logger Utility
 * ================================================================================
 *
 * مستويات الـ Logging:
 * - ERROR: أخطاء فقط (يظهر دائماً)
 * - WARN:  تحذيرات + أخطاء
 * - INFO:  معلومات + تحذيرات + أخطاء
 * - DEBUG: كل شيء (تفصيلي جداً)
 *
 * للتحكم في المستوى:
 * REACT_APP_LOG_LEVEL=DEBUG|INFO|WARN|ERROR
 * ================================================================================
 */

const LOG_LEVEL = process.env.REACT_APP_LOG_LEVEL || 'INFO';

const LEVELS = {
  ERROR: 0,
  WARN: 1,
  INFO: 2,
  DEBUG: 3
};

const currentLevel = LEVELS[LOG_LEVEL.toUpperCase()] ?? LEVELS.INFO;

// ─── Override console العالمي ────────────────────────────────────────────────
// يمنع أي console.log / console.info / console.warn / console.debug
// من الظهور خارج الـ logger (مكتبات خارجية، كود قديم، إلخ)
const _originalConsole = { ...console };

if (currentLevel < LEVELS.INFO) {
  console.log   = () => {};
  console.info  = () => {};
}

if (currentLevel < LEVELS.WARN) {
  console.warn  = () => {};
}

if (currentLevel < LEVELS.DEBUG) {
  console.debug = () => {};
}

// console.error يبقى كما هو دائماً
// ─────────────────────────────────────────────────────────────────────────────

/**
 * فلترة Warnings من React DevTools Extension
 */
const isDevToolsWarning = (args) => {
  const msg = args.join(' ');
  return (
    msg.includes('__installHook') ||
    msg.includes('__logger') ||
    msg.includes('overrideMethod') ||
    msg.includes('fetchLocationData')
  );
};

/**
 * تنسيق الرسالة مع التوقيت والمستوى
 */
const formatMessage = (level, ...args) => {
  const timestamp = new Date().toLocaleTimeString('ar-EG');
  const emoji = {
    ERROR: '❌',
    WARN:  '⚠️',
    INFO:  'ℹ️',
    DEBUG: '🔍'
  };
  return [`[${timestamp}] ${emoji[level]} ${level}:`, ...args];
};

/**
 * كائن Logger المتقدم
 */
export const logger = {

  /** أخطاء — تظهر دائماً بغض النظر عن المستوى */
  error: (...args) => {
    _originalConsole.error(...formatMessage('ERROR', ...args));
  },

  /** تحذيرات — تظهر في WARN وأعلى */
  warn: (...args) => {
    if (isDevToolsWarning(args)) return;
    if (currentLevel >= LEVELS.WARN) {
      _originalConsole.warn(...formatMessage('WARN', ...args));
    }
  },

  /** معلومات — تظهر في INFO وأعلى */
  info: (...args) => {
    if (currentLevel >= LEVELS.INFO) {
      _originalConsole.info(...formatMessage('INFO', ...args));
    }
  },

  /**
   * رسائل تفصيلية للتطوير — تظهر في DEBUG فقط
   * استخدمه بدلاً من console.log في أي مكان تريد إخفاءه على الإنتاج
   * مثال: logger.debug(`🔌 WebSocket for client ${id}:`, status);
   */
  debug: (...args) => {
    if (currentLevel >= LEVELS.DEBUG) {
      _originalConsole.log(...formatMessage('DEBUG', ...args));
    }
  },

  /** log عادي — يظهر في INFO وأعلى */
  log: (...args) => {
    if (currentLevel >= LEVELS.INFO) {
      _originalConsole.log(...args);
    }
  },

  /** جدول — يظهر في DEBUG فقط */
  table: (data) => {
    if (currentLevel >= LEVELS.DEBUG) {
      _originalConsole.table(data);
    }
  },

  /** بداية مجموعة — تظهر في DEBUG فقط */
  group: (label) => {
    if (currentLevel >= LEVELS.DEBUG) {
      _originalConsole.group(label);
    }
  },

  /** نهاية مجموعة */
  groupEnd: () => {
    if (currentLevel >= LEVELS.DEBUG) {
      _originalConsole.groupEnd();
    }
  },

  /** معلومات الـ Logger الحالي */
  getInfo: () => ({
    level:        LOG_LEVEL,
    numericLevel: currentLevel,
    errorEnabled: true,
    warnEnabled:  currentLevel >= LEVELS.WARN,
    infoEnabled:  currentLevel >= LEVELS.INFO,
    debugEnabled: currentLevel >= LEVELS.DEBUG
  })
};

// معلومات التهيئة — في بيئة التطوير فقط
if (process.env.NODE_ENV === 'development') {
  _originalConsole.log('🔧 Logger initialized:', logger.getInfo());
}

export default logger;